import type { AiContext } from '@/components/chat/types/ai-context';
import { tool } from 'ai';
import { z } from 'zod';

// Schema for focusCode tool
//
// `lineNumbers` accepts a string fallback in addition to the canonical array.
// Some thinking models (notably DeepSeek V4 Flash and Qwen-family providers) serialize
// array tool arguments as a comma-separated string ("84,86,95,100") even when the JSON
// schema declares an array. Without this fallback the SDK rejects the call, the model
// reasons again, retries, and burns an entire round-trip's worth of latency/cost per
// occurrence. Coercing here turns a hard failure into a transparent recovery.
// See chat investigation around DeepSeek V4 Flash overspending (May 2026).
const lineNumbersFromString = z.string().transform((value) =>
  value
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((parsed) => Number.isFinite(parsed)),
);

export const focusCodeSchema = z.object({
  filename: z.string().describe('The filename to scroll to (e.g. "src/components/Button.tsx")'),
  side: z
    .enum(['old', 'new'])
    .optional()
    .describe(
      'Optional diff side to target when highlighting lines. Use "new" for added/right-side lines and "old" for deleted/left-side lines',
    ),
  lineNumber: z
    .number()
    .optional()
    .describe('Optional single line number to scroll to within the file'),
  // Prefer the array form; the string variant exists only as a defensive fallback for
  // providers that misrender arrays as comma-separated strings (see comment above).
  lineNumbers: z
    .union([z.array(z.number()), lineNumbersFromString])
    .optional()
    .describe('Optional array of line numbers to highlight (e.g. [10, 11, 12] for a code block)'),
});

export type FocusCodeArgs = z.infer<typeof focusCodeSchema>;
type FocusCodeSide = FocusCodeArgs['side'];

type FocusCodeResultBase = {
  pendingAction: true;
  filename: string;
  side?: FocusCodeSide;
  lineNumber?: number;
  lineNumbers?: number[]; // Support for multiple lines
};

export type FocusCodeSuccessResult = FocusCodeResultBase & {
  success: true;
  error?: never;
};

export type FocusCodeErrorResult = FocusCodeResultBase & {
  success: false;
  error: string;
};

export type FocusCodeResult = FocusCodeSuccessResult | FocusCodeErrorResult;

/**
 * What a focusCode call resolves to against the PR page's files: the file it may scroll to, or
 * why it cannot. Pure, so a pull request page's chat session resolves its `focus_code` calls
 * in the browser the same way (`src/components/chat/code-session/`).
 */
export function resolveFocusCodeResult(
  context: Pick<AiContext, 'codeContent'>,
  args: FocusCodeArgs,
): FocusCodeResult {
  // Validate that the file exists in the PR context
  const availableFiles = context.codeContent?.map((file) => file.filename) || [];

  // Check if context is still loading or empty
  if (!context.codeContent || context.codeContent.length === 0) {
    return {
      pendingAction: true,
      filename: args.filename,
      side: args.side,
      lineNumber: args.lineNumber,
      lineNumbers: args.lineNumbers,
      success: false,
      error: `Navigation failed: PR files are still loading. Please try again in a moment.`,
    };
  }

  if (!availableFiles.includes(args.filename)) {
    // Try to find a similar filename
    const lowerFilename = args.filename.toLowerCase();
    const similarFile = availableFiles.find(
      (file) =>
        file.toLowerCase().includes(lowerFilename) ||
        file.toLowerCase().endsWith(lowerFilename.split('/').pop() || ''),
    );

    if (similarFile) {
      return {
        pendingAction: true,
        filename: args.filename,
        side: args.side,
        lineNumber: args.lineNumber,
        lineNumbers: args.lineNumbers,
        success: false,
        error: `File not found in PR. Did you mean: ${similarFile}?`,
      };
    }

    return {
      pendingAction: true,
      filename: args.filename,
      side: args.side,
      lineNumber: args.lineNumber,
      lineNumbers: args.lineNumbers,
      success: false,
      error: `File not found in PR changes. Available files: ${availableFiles.slice(0, 5).join(', ')}${availableFiles.length > 5 ? '...' : ''}`,
    };
  }

  // Return the file information for the client to handle
  return {
    pendingAction: true,
    filename: args.filename,
    side: args.side,
    lineNumber: args.lineNumber,
    lineNumbers: args.lineNumbers,
    success: true,
  };
}

export const createFocusCodeTool = (context: AiContext) =>
  tool({
    inputSchema: focusCodeSchema,
    description: `Navigate to and optionally highlight specific code in the PR changes view. Use this tool to:
- Navigate to a file when discussing its overall changes or structure
- Highlight a specific line when explaining a bug, implementation detail, or answering "where is X?"
- Highlight multiple lines when showing a complete function, class, or code block
- Direct user attention during code explanations or reviews

Usage guidelines:
- This tool does not read or verify code. If the answer needs exact code, diff content, line mapping, or current PR contents, first use readChangedFile or the bash artifact fallback, then optionally call focusCode.
- For file-level discussions: Navigate without line numbers
- For specific issues or bugs: Highlight the exact problematic line(s)
- For showing implementations: Highlight the entire function/method/class
- When explaining code flow: Highlight relevant sections as you explain
- When highlighting added/right-side lines, set side to "new"
- When highlighting deleted/left-side lines, set side to "old"

Examples:
- User: "Where is the auth logic?" → Navigate to auth file and highlight the relevant function
- User: "What changed in config.js?" → Navigate to config.js without line highlighting
- User: "Why is this failing?" → Highlight the specific problematic lines
- Added-only line on the right side → { filename: "...", side: "new", lineNumber: 12 }
- Deleted-only line on the left side → { filename: "...", side: "old", lineNumber: 40 }`,
    execute: async (args: FocusCodeArgs): Promise<FocusCodeResult> =>
      resolveFocusCodeResult(context, args),
  });
